import React, { useEffect, useRef } from "react";

import { NetworkStatus, useReactiveVar } from "@apollo/client";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import uniqBy from "lodash/uniqBy";

import { ValidationState, ModelType } from "../../../API";
import {
  useGetHumanValidatorTableData,
  type PendingEventRow,
} from "../hooks/useHumanValidatorTableData";
import { useTakeToValidate } from "../hooks/useTakeToValidate";
import { humanValidatorTableVariable } from "../variables/humanValidatorTable";
import { handleApolloError } from "../../../common/utils/handleApolloError";
import { ApolloError } from "@apollo/client";
import { HV_PAGE_SIZE } from "../../../common/variables/common";

const HumanValidatorTable = (): JSX.Element => {
  const { tableData, showDetails } = useReactiveVar(
    humanValidatorTableVariable
  );

  const { rows, nextToken, loading, networkStatus, fetchMore } =
    useGetHumanValidatorTableData();

  const { takeToValidate } = useTakeToValidate();

  const apiMutationTakeEventToValidate = (
    row: PendingEventRow,
    customerId: string,
    eventId: string
  ): void => {
    takeToValidate({
      variables: {
        customerId,
        eventId,
      },
    })
      .then((response): void => {
        humanValidatorTableVariable({
          tableData: {
            ...row,
            ...response.data?.takeEventToValidate,
          },
          showDetails: true,
        });
      })
      .catch((error): void => {
        console.error(error);
      });
  };

  const handleRowClick = (
    _event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: PendingEventRow
  ): void => {
    if (showDetails) {
      return;
    }

    apiMutationTakeEventToValidate(row, row.customerId, row.eventId);
  };

  const handleFetchMore = async () => {
    if (!nextToken || loading || networkStatus === NetworkStatus.fetchMore)
      return;

    try {
      await fetchMore({
        variables: { nextToken, limit: HV_PAGE_SIZE },
        updateQuery: (pv, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getEventsPendingHumanValidation?.items)
            return pv;

          const curr = pv?.getEventsPendingHumanValidation?.items ?? [];
          const more =
            fetchMoreResult.getEventsPendingHumanValidation.items ?? [];

          const items = uniqBy([...curr, ...more], item => item?.id);
          const fireItems = items.filter(i => i?.serviceId === "fire");
          const otherItems = items
            .filter(i => i?.serviceId !== "fire")
            .sort((a, b) =>
              (a?.serviceId ?? "").localeCompare(b?.serviceId ?? "")
            );

          return {
            getEventsPendingHumanValidation: {
              ...(pv?.getEventsPendingHumanValidation ?? {}),
              items: fireItems.concat(otherItems),
              nextToken:
                fetchMoreResult.getEventsPendingHumanValidation.nextToken ??
                null,
            },
          };
        },
      });
    } catch (error) {
      handleApolloError(error as ApolloError);
    }
  };

  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!nextToken || loading || networkStatus === NetworkStatus.fetchMore)
      return;

    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;

    void handleFetchMore();
  }, [nextToken, loading, networkStatus, handleFetchMore]);

  useEffect(() => {
    if (networkStatus !== NetworkStatus.fetchMore)
      loadingMoreRef.current = false;
  }, [networkStatus]);

  const networkStatusChanged = [NetworkStatus.fetchMore].includes(
    networkStatus
  );

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Event ID</TableCell>
          <TableCell align="right">Location</TableCell>
          <TableCell align="right">Date</TableCell>
          <TableCell align="right">Model</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {React.Children.toArray(
          rows.map((row): JSX.Element => {
            const date = row.date ? new Date(row.date).toLocaleString() : "n/a";

            const renameGL =
              row.model === ModelType.gasleak ? "leak" : row.model;

            return (
              <React.Fragment key={row.rowId}>
                <TableRow
                  key={row.rowId}
                  hover
                  selected={row.rowId === tableData?.rowId}
                  onClick={(event): void => handleRowClick(event, row)}
                  sx={{
                    cursor:
                      showDetails ||
                      row.validationState === ValidationState.IN_VALIDATION
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <TableCell>{row.rowId}</TableCell>
                  <TableCell align="right" sx={{ wordBreak: "break-all" }}>
                    {row.locationId.split("#").pop()}
                  </TableCell>
                  <TableCell align="right">{date}</TableCell>
                  <TableCell align="right">{renameGL}</TableCell>
                </TableRow>
              </React.Fragment>
            );
          })
        )}

        {nextToken && (
          <TableRow key={`sentinel-${nextToken}`}>
            <TableCell colSpan={4}></TableCell>
          </TableRow>
        )}

        {(loading || networkStatusChanged) && (
          <TableRow>
            <TableCell colSpan={3}>
              {[...Array(3)].map(
                (_, index): JSX.Element => (
                  <Skeleton key={index} variant="rectangular" sx={{ my: 3 }} />
                )
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default HumanValidatorTable;
